/**
 * What a group change did to people's assignments, in words (plan
 * docs/plans/lms-plan.md 8.2 "profile reconcile ... run on every membership or
 * requirement change", 18.20, 18.21; WP-43).
 *
 * Every membership, requirement or activation change answers with the
 * reconcile's summary, or { failed: true, code } when it could not run. A
 * failed reconcile is a WARNING, not an error: the change is saved and the
 * nightly pass (WP-36) catches the assignments up (18.20).
 */
import { reconcileFailed } from '../../../api/groups';
import type { ReconcileResult } from '../../../api/groups';

/** The Groups sub-view's own address (Manage tab, sub-view path "groups"; plan 18.3). */
export const GROUPS_BASE = '/lms/manage/groups';

export function groupHref(groupId: string): string {
  return `${GROUPS_BASE}/${encodeURIComponent(groupId)}`;
}

function plural(n: number, one: string, many: string): string {
  return `${n.toLocaleString()} ${n === 1 ? one : many}`;
}

/**
 * One sentence for a reconcile summary, or null when it changed nothing
 * worth saying (null in, null out; a failed one is the caller's warning).
 */
export function describeReconcile(r: ReconcileResult | undefined): string | null {
  if (!r || reconcileFailed(r)) return null;
  const parts: string[] = [];
  if (r.created > 0) parts.push(`${plural(r.created, 'assignment', 'assignments')} created`);
  if (r.cancelled > 0) parts.push(`${plural(r.cancelled, 'open assignment', 'open assignments')} cancelled`);
  if ((r.cancelled_no_access ?? 0) > 0) {
    parts.push(`${plural(r.cancelled_no_access ?? 0, 'assignment', 'assignments')} cancelled for people who left every site`);
  }
  if ((r.skipped_courses ?? 0) > 0) {
    parts.push(`${plural(r.skipped_courses ?? 0, 'required course', 'required courses')} not assigned (not published yet, or retired)`);
  }
  if (parts.length === 0) return null;
  return `${parts.join(', ')}.`;
}

/** The warning for a reconcile that could not run (18.20): the change stands. */
export function reconcileWarning(r: ReconcileResult | undefined): string | null {
  if (!reconcileFailed(r)) return null;
  return `Saved, but the assignments for this change have not caught up yet (${r.code}). The nightly pass will create or cancel them.`;
}
